import Box from '@mui/material/Box';
import { NameCell } from '@openx/components-core/lib/NameCell/NameCell';
import type { UseGetProvidersTableData } from '@openx/components-provider/lib/utils/context/ProviderHookTypes';
import type { ProvidersList } from '@openx/types/provider';
import type { Columns } from '@openx/types/tableHelpers';
import { sortMenuButtonItems } from '@openx/utils/lib/tableHelpers';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useProviderDeleteModal } from './useProviderDeleteModal';

export const useGetProvidersTableData: UseGetProvidersTableData = (searchPhrase: string | undefined) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleDeleteProvider = useProviderDeleteModal();

  const tableColumns: Columns<ProvidersList> = useMemo(
    () => [
      {
        ellipsis: true,
        ellipsisMaxWidth: '650px',
        key: 'name',
        render: ({ id, name }) => (
          <NameCell name={name} id={id} pathTo={`/providers/${id}/details`} searchPhrase={searchPhrase} />
        ),
        title: t('Name'),
        width: '60%',
      },
      {
        key: 'allowed_accounts',
        render: ({ allowed_accounts }) => <Box component={'span'}>{allowed_accounts?.length}</Box>,
        title: t('Linked Organization'),
        width: '40%',
      },
    ],
    [searchPhrase, t]
  );

  const rowActions = useMemo(
    () =>
      sortMenuButtonItems({
        DELETE: {
          color: 'error',
          danger: true,
          'data-test': 'delete-action-button',
          label: t('Delete'),
          onClick: handleDeleteProvider,
        },
        DETAILS: {
          'data-test': 'details-action-button',
          label: t('Details'),
          onClick: ({ id }) => {
            navigate(`/providers/${id}/details`);
          },
        },
        EDIT: {
          'data-test': 'edit-action-button',
          label: t('Edit'),
          onClick: ({ id }) => {
            navigate(`/providers/${id}/edit`);
          },
        },
      }),
    [handleDeleteProvider, navigate, t]
  );

  return {
    rowActions,
    tableColumns,
  };
};
